import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaBars, FaTimes, FaSignOutAlt } from 'react-icons/fa';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Closed by default on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true); // Always open on desktop
      } else {
        setSidebarOpen(false); // Closed by default on mobile
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: '📊', section: 'main' },
    { name: 'Products', path: '/products', icon: '📦', section: 'main' },
    { name: 'Orders', path: '/orders', icon: '🛒', section: 'main' },
    { name: 'AI Chat', path: '/chat', icon: '💬', section: 'main' },
    { name: 'Calculator', path: '/calculator', icon: '🧮', section: 'main' },
    { name: 'Profit Tracker', path: '/profit-tracker', icon: '💰', section: 'pro', pro: true },
    { name: 'Predictive Alerts', path: '/predictive-alerts', icon: '🔔', section: 'pro', pro: true },
    { name: 'Money Recovery', path: '/money-recovery', icon: '💵', section: 'pro', pro: true },
    { name: 'Amazon Ads', path: '/amazon-ads', icon: '📢', section: 'pro', pro: true },
    { name: 'Settings', path: '/settings', icon: '⚙️', section: 'account' },
    { name: 'Subscription', path: '/subscription', icon: '💳', section: 'account' }
  ];

  const mainNav = navigation.filter(item => item.section === 'main');
  const proNav = navigation.filter(item => item.section === 'pro');
  const accountNav = navigation.filter(item => item.section === 'account');

  const isPro = user?.subscription?.plan === 'pro';

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 glass shadow-soft transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <h1 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Genia+ SaaS</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-purple-600 p-2 transition-colors"
              aria-label="Close sidebar"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
            {/* Main Navigation */}
            <div className="space-y-1">
              {mainNav.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'gradient-primary text-white font-semibold shadow-soft'
                      : 'text-gray-700 hover:bg-purple-50 hover:text-purple-600'
                  }`}
                >
                  <span className="text-lg lg:text-xl mr-3">{item.icon}</span>
                  <span className="text-sm lg:text-base">{item.name}</span>
                </Link>
              ))}
            </div>

            {/* Pro Features */}
            <div>
              <div className="flex items-center px-4 mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Pro Features
                </span>
                {!isPro && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full font-bold">
                    PRO
                  </span>
                )}
              </div>
              <div className="space-y-1">
                {proNav.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'gradient-secondary text-white font-semibold shadow-soft'
                        : 'text-gray-700 hover:bg-pink-50 hover:text-pink-600'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-lg lg:text-xl mr-3">{item.icon}</span>
                      <span className="text-sm lg:text-base">{item.name}</span>
                    </div>
                    {!isPro && (
                      <span className="text-xs">🔒</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Account */}
            <div>
              <div className="px-4 mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Account
                </span>
              </div>
              <div className="space-y-1">
                {accountNav.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                      isActive(item.path)
                        ? 'gradient-success text-white font-semibold shadow-soft'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    <span className="text-lg lg:text-xl mr-3">{item.icon}</span>
                    <span className="text-sm lg:text-base">{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* User info */}
          <div className="p-4 border-t">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                <p className="text-xs text-blue-600 font-medium mt-1 capitalize">
                  Plan: {user?.subscription?.plan || 'N/A'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="ml-2 p-2 text-gray-400 hover:text-red-600 transition-colors"
                title="Logout"
                aria-label="Logout"
              >
                <FaSignOutAlt className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="glass shadow-soft sticky top-0 z-30 border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-500 hover:text-purple-600 p-2 lg:hidden transition-colors"
              aria-label="Toggle sidebar"
            >
              <FaBars className="text-xl" />
            </button>
            <div className="hidden lg:block">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-500 hover:text-purple-600 p-2 transition-colors"
                aria-label="Toggle sidebar"
              >
                <FaBars className="text-xl" />
              </button>
            </div>
            <div className="flex items-center space-x-2 lg:space-x-4">
              <span className="text-xs lg:text-sm font-medium bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent truncate max-w-[150px] lg:max-w-none">
                Welcome, {user?.firstName}! 👋
              </span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
